import { useState } from "react";
import {
  Loader2,
  Plus,
  X,
  Save,
  Trash2,
  FileInput,
  Copy,
  ExternalLink,
  Power,
  Code2,
} from "lucide-react";
import {
  useFormularios,
  useCreateFormulario,
  useUpdateFormulario,
  useDeleteFormulario,
  useListas,
  useAutomatizaciones,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Formulario, FormularioFormData } from "../types";

const EMPTY: FormularioFormData = {
  nombre: "",
  titulo: "",
  descripcion: "",
  fuente: "Formulario web",
  mensaje_exito: "¡Gracias! Te contactaremos pronto.",
  activo: true,
};

export function FormulariosPage() {
  const { emisorId } = useMarketingStore();
  const { data: forms, isLoading } = useFormularios(emisorId);
  const { data: listas } = useListas(emisorId);
  const { data: autos } = useAutomatizaciones(emisorId);

  const createMut = useCreateFormulario();
  const updateMut = useUpdateFormulario();
  const deleteMut = useDeleteFormulario();

  const [editing, setEditing] = useState<Formulario | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormularioFormData>(EMPTY);
  const [embedFor, setEmbedFor] = useState<Formulario | null>(null);
  const [copied, setCopied] = useState(false);

  const publicUrl = (slug: string) => `${window.location.origin}/f/${slug}`;

  function openCreate() {
    setForm(EMPTY);
    setCreating(true);
    setEditing(null);
  }
  function openEdit(f: Formulario) {
    setForm({
      nombre: f.nombre,
      titulo: f.titulo ?? "",
      descripcion: f.descripcion ?? "",
      fuente: f.fuente,
      lista_id: f.lista_id,
      automatizacion_id: f.automatizacion_id,
      mensaje_exito: f.mensaje_exito ?? "",
      activo: f.activo,
    });
    setEditing(f);
    setCreating(false);
  }
  function close() {
    setEditing(null);
    setCreating(false);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload: form });
    } else {
      await createMut.mutateAsync({ emisorId, payload: form });
    }
    close();
  }

  function copyEmbed(f: Formulario) {
    const code = `<iframe src="${publicUrl(f.slug)}" width="100%" height="520" frameborder="0" style="border:0;border-radius:16px"></iframe>`;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Formularios de captación de leads embebibles."
        right={
          <button
            onClick={openCreate}
            disabled={!emisorId}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition self-end disabled:opacity-50"
          >
            <Plus size={16} /> Nuevo formulario
          </button>
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {forms && (
        <div className="flex-1 min-h-0 overflow-auto">
          {forms.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-50 grid place-items-center mb-3">
                <FileInput size={28} className="text-brand-700" />
              </div>
              <p className="font-bold text-slate-700">Aún no tienes formularios</p>
              <p className="text-sm text-slate-400 mt-1 max-w-md">
                Crea formularios para tu web que capturan leads automáticamente con su origen UTM,
                los añaden a una lista y disparan una automatización.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forms.map((f: Formulario) => (
                <div key={f.id} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${f.activo ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-400"}`}>
                        <FileInput size={20} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-black text-slate-900 truncate">{f.nombre}</div>
                        <div className="text-xs text-slate-500">{f.envios_count} envíos</div>
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${f.activo ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {f.activo ? "Activo" : "Inactivo"}
                    </span>
                  </div>

                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-[11px] font-mono text-slate-500 truncate">
                    /f/{f.slug}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <button
                      onClick={() => window.open(publicUrl(f.slug), "_blank")}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 text-xs font-bold text-slate-700"
                    >
                      <ExternalLink size={13} /> Abrir
                    </button>
                    <button
                      onClick={() => setEmbedFor(f)}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 text-xs font-bold text-slate-700"
                    >
                      <Code2 size={13} /> Embed
                    </button>
                    <button
                      onClick={() => openEdit(f)}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 text-xs font-bold text-slate-700"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => updateMut.mutate({ id: f.id, payload: { activo: !f.activo } })}
                      title={f.activo ? "Desactivar" : "Activar"}
                      className="inline-flex items-center rounded-lg border border-slate-200 hover:bg-slate-50 px-2 py-1.5 text-slate-600"
                    >
                      <Power size={13} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Eliminar "${f.nombre}"?`)) deleteMut.mutate(f.id);
                      }}
                      className="inline-flex items-center rounded-lg border border-slate-200 hover:bg-rose-50 hover:text-rose-600 px-2 py-1.5 text-slate-400"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal embed */}
      {embedFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Insertar en tu web</h2>
              <button onClick={() => setEmbedFor(null)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <p className="text-sm text-slate-500 mb-2">
              Copia este código y pégalo en el HTML de tu página:
            </p>
            <pre className="rounded-xl bg-slate-900 text-emerald-300 p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all">
{`<iframe src="${publicUrl(embedFor.slug)}" width="100%" height="520" frameborder="0" style="border:0;border-radius:16px"></iframe>`}
            </pre>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                O comparte el enlace directo:{" "}
                <a href={publicUrl(embedFor.slug)} target="_blank" rel="noreferrer" className="text-brand-700 font-bold">
                  {publicUrl(embedFor.slug)}
                </a>
              </span>
              <button
                onClick={() => copyEmbed(embedFor)}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-3 py-2 text-sm font-bold"
              >
                <Copy size={14} /> {copied ? "¡Copiado!" : "Copiar código"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[92vh] overflow-auto">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? "Editar formulario" : "Nuevo formulario"}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre interno *</label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="ej: Contacto landing servicios"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Título visible</label>
                <input
                  type="text"
                  value={form.titulo ?? ""}
                  onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Solicita más información"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Descripción</label>
                <input
                  type="text"
                  value={form.descripcion ?? ""}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fuente</label>
                  <input
                    type="text"
                    value={form.fuente ?? ""}
                    onChange={(e) => setForm({ ...form, fuente: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Añadir a lista</label>
                  <select
                    value={form.lista_id ?? ""}
                    onChange={(e) => setForm({ ...form, lista_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— ninguna —</option>
                    {listas?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Disparar automatización</label>
                <select
                  value={form.automatizacion_id ?? ""}
                  onChange={(e) => setForm({ ...form, automatizacion_id: e.target.value ? Number(e.target.value) : null })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">— ninguna —</option>
                  {autos?.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mensaje de éxito</label>
                <input
                  type="text"
                  value={form.mensaje_exito ?? ""}
                  onChange={(e) => setForm({ ...form, mensaje_exito: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <p className="text-xs text-slate-400">
                Los campos del formulario (nombre, email, empresa, teléfono) son los estándar de
                captación. El lead se crea con estado "nuevo" y la fuente indicada.
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">
                Cancelar
              </button>
              <button type="submit" disabled={createMut.isPending || updateMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
